import { useEffect, useRef } from 'react';
import {
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { useContext } from 'react';
import { API_SERVICE } from '@/components/candidate-job-slider-v1/api';
import { CandidateContext } from '@/components/candidate-job-slider-v1/context/index';
import { ApplicationData } from '@/components/candidate-job-slider-v1/types/application.interface';
import { useShowToast } from '@/hooks';

const SUPPORT_ERROR_MESSAGE =
  'Something went wrong. Please contact support@kula.ai if this continues.';

/** Keep in sync with `useQuery` below (slider + list prefetch). */
export const APPLICATION_QUERY_STALE_MS = 5 * 60 * 1000;

export function getApplicationQueryKey(
  applicationId: string | null | undefined
): [string] {
  if (
    applicationId === null ||
    applicationId === '' ||
    String(applicationId) === 'null'
  ) {
    return ['application/__none__'];
  }
  return [`application/${applicationId}`];
}

/** Warm cache for the job-details application drawer (`include[]=stage_actions`). */
export function prefetchApplication(
  queryClient: QueryClient,
  applicationId: string | number | null | undefined
): Promise<void> {
  const id =
    applicationId === null || applicationId === ''
      ? null
      : String(applicationId);
  if (!id || id === 'null') {
    return Promise.resolve();
  }
  const queryKey = getApplicationQueryKey(id);
  return queryClient.prefetchQuery({
    queryKey,
    queryFn: () =>
      API_SERVICE.getApplication(id, {
        include: ['stage_actions'],
      }),
    staleTime: APPLICATION_QUERY_STALE_MS,
  });
}

function getResponseStatus(error: unknown): number {
  const err = error as { response?: { status?: number } };
  return err?.response?.status ?? 0;
}

function isServerError(error: unknown): boolean {
  const status = getResponseStatus(error);
  return status >= 500 && status < 600;
}

export const useApplicationHelper = () => {
  const context = useContext(CandidateContext) as {
    transferredApplicationOverride?: {
      applicationData: any;
      refetchApplication: () => void;
    } | null;
  };
  const applicationId = window.sessionStorage.getItem('candidate-app-id');
  const queryClient = useQueryClient();
  const toast = useShowToast();
  const hasShownErrorToast = useRef(false);

  useEffect(() => {
    hasShownErrorToast.current = false;
  }, [applicationId]);

  const queryKey = getApplicationQueryKey(applicationId);
  const {
    data: applicationDataFromQuery,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery<ApplicationData>({
    queryKey,
    queryFn: () =>
      API_SERVICE.getApplication(applicationId as string, {
        include: ['stage_actions'],
      }),
    staleTime: APPLICATION_QUERY_STALE_MS,
    enabled:
      !context?.transferredApplicationOverride &&
      applicationId !== 'null' &&
      !!applicationId &&
      queryKey[0] !== 'application/__none__',
    retry: (failureCount, err) =>
      isServerError(err) ? false : failureCount < 3,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (
      isError &&
      error &&
      isServerError(error) &&
      !hasShownErrorToast.current
    ) {
      hasShownErrorToast.current = true;
      toast({ title: SUPPORT_ERROR_MESSAGE, status: 'error' });
    }
  }, [isError, error, toast]);

  const refetchApplication = async () => {
    if (context?.transferredApplicationOverride) {
      context.transferredApplicationOverride.refetchApplication();
      return;
    }
    if (!applicationId) return;
    hasShownErrorToast.current = false;
    await queryClient.invalidateQueries({ queryKey });
  };

  const clearPreviousApplicationCache = (previousID: string) => {
    if (!previousID) return;
    hasShownErrorToast.current = false;
    queryClient.removeQueries({
      queryKey: getApplicationQueryKey(String(previousID)),
    });
  };

  if (context?.transferredApplicationOverride) {
    return {
      applicationData: {
        ...context.transferredApplicationOverride.applicationData,
        transferred: true,
      },
      isLoading: false,
      isFetching: false,
      refetchApplication,
      clearPreviousApplicationCache,
    };
  }

  return {
    applicationData: applicationDataFromQuery,
    isLoading,
    isFetching,
    isError,
    isServerError: isError && isServerError(error),
    refetchApplication,
    clearPreviousApplicationCache,
  };
};
